import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { motion } from 'framer-motion';

interface AffordabilityCalculatorProps {
    initialSalary?: number;
}

const COLORS = ['#001b4a', '#f1ae24']; // Navy and Gold

const AffordabilityCalculator: React.FC<AffordabilityCalculatorProps> = ({ initialSalary = 100000 }) => {
    const [monthlySalary, setMonthlySalary] = useState<number>(initialSalary);
    const [downPayment, setDownPayment] = useState<number>(1000000);
    const [tenure, setTenure] = useState<number>(20);
    const [interestRate, setInterestRate] = useState<number>(8.5);
    const [otherEmis, setOtherEmis] = useState<number>(0);

    // Dynamic Calculations
    const results = useMemo(() => {
        const monthlyInterest = interestRate / 12 / 100;
        const numberOfMonths = tenure * 12;
        
        // Typical FOIR (Fixed Obligation to Income Ratio) is 50% of monthly income
        const maxEmiPossible = (monthlySalary * 0.5) - otherEmis;
        
        if (maxEmiPossible <= 0) {
            return {
                eligibility: 0,
                propertyCost: downPayment,
                maxEmi: 0
            };
        }

        // Loan Amount = EMI * [(1+R)^n - 1] / [R * (1+R)^n]
        const loanAmount = maxEmiPossible * ((Math.pow(1 + monthlyInterest, numberOfMonths) - 1) / (monthlyInterest * Math.pow(1 + monthlyInterest, numberOfMonths)));
        
        return {
            eligibility: Math.round(loanAmount),
            propertyCost: Math.round(loanAmount + downPayment),
            maxEmi: Math.round(maxEmiPossible)
        };
    }, [monthlySalary, downPayment, tenure, interestRate, otherEmis]);

    const formatCurrency = (val: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0,
        }).format(val);
    };

    const formatShortCurrency = (amount: number) => {
        if (amount >= 10000000) return `₹ ${(amount / 10000000).toFixed(2)} Cr`;
        if (amount >= 100000) return `₹ ${(amount / 100000).toFixed(2)} L`;
        return `₹ ${amount.toLocaleString('en-IN')}`;
    };

    const chartData = [
        { name: 'Loan Amount', value: results.eligibility },
        { name: 'Down Payment', value: downPayment },
    ];

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Main Calculator Card */}
            <div className="bg-white rounded-xl border border-slate-200 purple-card-shadow overflow-hidden relative">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-[#001b4a] via-[#f1ae24] to-[#001b4a]"></div>
                
                <div className="grid grid-cols-1 lg:grid-cols-12">
                    {/* Left Side: Inputs */}
                    <div className="lg:col-span-7 p-6 md:p-10 border-r border-slate-100">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-900 shadow-sm border border-slate-200">
                                <i className="fa-solid fa-wallet text-base"></i>
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 leading-none">Affordability Inputs</h3>
                                <p className="text-xs font-medium text-slate-400 mt-1">Estimate your maximum property budget</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* Monthly Salary */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Monthly Salary (Net)</label>
                                    <div className="relative group">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">₹</span>
                                        <input 
                                            type="number" 
                                            value={monthlySalary} 
                                            onChange={(e) => setMonthlySalary(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl pl-7 pr-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-40 text-sm"
                                        />
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="10000" 
                                    max="1000000" 
                                    step="5000" 
                                    value={monthlySalary} 
                                    onChange={(e) => setMonthlySalary(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>₹ 10K</span>
                                    <span>₹ 10L</span>
                                </div>
                            </div>

                            {/* Down Payment */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Down Payment</label>
                                    <div className="relative group">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">₹</span>
                                        <input 
                                            type="number" 
                                            value={downPayment} 
                                            onChange={(e) => setDownPayment(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl pl-7 pr-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-40 text-sm"
                                        />
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="0" 
                                    max="50000000" 
                                    step="100000" 
                                    value={downPayment} 
                                    onChange={(e) => setDownPayment(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>₹ 0</span>
                                    <span>₹ 5Cr</span>
                                </div>
                            </div>

                            {/* Interest Rate */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Expected Rate (%)</label>
                                    <div className="relative group">
                                        <input 
                                            type="number" 
                                            value={interestRate} 
                                            step="0.1"
                                            onChange={(e) => setInterestRate(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-24 text-sm"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">%</span>
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="5" 
                                    max="15" 
                                    step="0.1" 
                                    value={interestRate} 
                                    onChange={(e) => setInterestRate(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>5%</span>
                                    <span>15%</span>
                                </div>
                            </div>

                            {/* Tenure */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Tenure (Years)</label>
                                    <div className="relative group">
                                        <input 
                                            type="number" 
                                            value={tenure} 
                                            onChange={(e) => setTenure(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-24 text-sm"
                                        />
                                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-[11px]">Yrs</span>
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="1" 
                                    max="30" 
                                    step="1" 
                                    value={tenure} 
                                    onChange={(e) => setTenure(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>1 Year</span>
                                    <span>30 Years</span>
                                </div>
                            </div>

                            {/* Existing EMIs */}
                            <div className="space-y-2">
                                <div className="flex justify-between items-end">
                                    <label className="text-sm font-semibold text-slate-700">Other monthly EMIs</label>
                                    <div className="relative group">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm">₹</span>
                                        <input 
                                            type="number" 
                                            value={otherEmis} 
                                            onChange={(e) => setOtherEmis(Number(e.target.value))}
                                            className="bg-slate-50 border border-slate-200 rounded-xl pl-7 pr-4 py-2 text-right font-bold text-slate-900 focus:ring-2 focus:ring-[#f1ae24] focus:border-[#f1ae24] outline-none transition-all w-40 text-sm"
                                        />
                                    </div>
                                </div>
                                <input 
                                    type="range" 
                                    min="0" 
                                    max="500000" 
                                    step="1000" 
                                    value={otherEmis} 
                                    onChange={(e) => setOtherEmis(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-[#001b4a]"
                                />
                                <div className="flex justify-between text-[11px] font-medium text-slate-400">
                                    <span>₹ 0</span>
                                    <span>₹ 5L</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: Insights & Summary */}
                    <div className="lg:col-span-5 bg-slate-50/50 p-6 md:p-8 flex flex-col gap-4 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-slate-200/50 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                        
                        <div className="relative z-10 w-full">
                            <div className="bg-white p-6 rounded-2xl text-slate-900 shadow-sm mb-4 border border-slate-200 group transition-all duration-500 w-full relative overflow-hidden">
                                <p className="text-slate-500 font-semibold text-xs mb-2 text-center uppercase tracking-wide">Max Property Budget</p>
                                <h2 className="text-3xl md:text-5xl font-bold text-[#001b4a] text-center mb-2 leading-none">
                                    {formatShortCurrency(results.propertyCost)}
                                </h2>
                                <div className="w-full h-px bg-slate-100 my-4"></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="text-center">
                                        <p className="text-slate-400 text-[11px] font-medium uppercase tracking-wider mb-1 leading-none">Loan Eligibility</p>
                                        <p className="font-bold text-slate-800 text-base">{formatShortCurrency(results.eligibility)}</p>
                                    </div>
                                    <div className="text-center border-l border-slate-100">
                                        <p className="text-slate-400 text-[11px] font-medium uppercase tracking-wider mb-1 leading-none">Allowable EMI</p>
                                        <p className="font-bold text-[#f1ae24] text-base">{formatCurrency(results.maxEmi)}</p>
                                    </div>
                                </div>
                            </div>

                            <Link 
                                to="/login" 
                                className="block w-full post-property-btn-effect text-white p-4 rounded-2xl text-center shadow-lg transition-all active:scale-95 group relative overflow-hidden mb-6"
                            >
                                <div className="shimmer-effect-layer"></div>
                                <div className="flex items-center justify-center gap-2 relative z-10">
                                    <i className="fa-solid fa-plus-circle text-lg"></i>
                                    <span className="font-bold uppercase tracking-wide text-xs">Post Your Property Free</span>
                                </div>
                                <p className="text-[10px] font-medium opacity-80 mt-1 relative z-10">Join 10,000+ happy sellers today!</p>
                            </Link>

                            {/* Breakdown Chart */}
                            <div className="w-full flex flex-col items-center py-4">
                                <div className="h-[200px] md:h-[230px] w-full">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={chartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={50}
                                                outerRadius={80}
                                                paddingAngle={8}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {chartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip 
                                                formatter={(value: any) => formatCurrency(Number(value))}
                                                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', fontWeight: 'bold' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                
                                <div className="flex flex-wrap justify-center gap-4 md:gap-6 mt-6 w-full">
                                    {chartData.map((item, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                                            <span className="text-[11px] font-semibold text-slate-600 tracking-wide">{item.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                input[type='range']::-webkit-slider-runnable-track {
                    background: transparent;
                }
                input[type='range']::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    background: #f1ae24;
                    border: 3px solid #001b4a;
                    border-radius: 50%;
                    cursor: pointer;
                    margin-top: -8px;
                    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
                }
                .recharts-legend-wrapper {
                    display: none !important;
                }
            `}</style>
        </div>
    );
};

export default AffordabilityCalculator;
